import type { Database } from '../client'
import type { SelectMembership, SelectMembershipAddOn } from '../schema'
import type { AddOnPool } from './mint-add-on-seats'
import { mintAddOnSeatsInTransaction } from './mint-add-on-seats'
import { mintPoolInTransaction } from './mint-purchased-seats'

export interface MintRenewalSeatsArgs {
  invoiceId: string
  organizationId: string
  /** Every seat the renewal bought — continuing students and empty seats together. */
  seats: number
  /**
   * How many of `seats` are EMPTY seats that start at payment rather than at the handover.
   *
   * Zero means the whole order is minted into the new pool, which is the pre-bridge behaviour and
   * what a renewal of a lapsed pool (nothing to ride today) or an invoice created before the bridge
   * existed still gets.
   */
  emptySeats: number
  /**
   * The pools the empty seats ride BEFORE the new one opens, in time order: the live pool, then any
   * term queued between it and the pool being renewed. Recorded by the checkout in
   * `invoice_membership`, so what is minted here is exactly what was charged.
   */
  bridgePools: AddOnPool[]
  /** The new pool's window. */
  startDate: Date
  endDate: Date
  /** When the invoice was paid. The empty seats start here, never earlier. */
  paidAt: Date
}

export interface MintRenewalSeatsResult {
  membership: SelectMembership
  /** The chain the empty seats ride, earliest segment first. Empty when nothing was bridged. */
  addOns: SelectMembershipAddOn[]
  /** The pool's own seats plus one row per empty seat per segment. */
  seatsCreated: number
  /** Continuing students stamped onto the pool's own seats, in seat order. */
  assignedChildIds: string[]
  /** Continuing students who no longer qualify (left the school, or seated since). */
  skippedChildIds: string[]
  releasedTrialSeatIds: string[]
}

/**
 * Fulfil a RENEWAL: the new pool with its continuing students, and — when the order carried empty
 * seats and the school holds a live term to ride — those seats as an add-on chain that starts at
 * payment and runs to the end of the new pool.
 *
 * A renewal used to mint every seat it bought into the new pool, so a school renewing on 29 Sep
 * with three seats to spare paid for them in September and could not hand them out until the
 * current term closed the following September. The empty seats now behave exactly like a top-up
 * placed the same day: one `MembershipAddOn` segment per pool from the live one through the new
 * one, each riding that pool's window, so `getOrgSeatGroups` reads them as ONE purchase spanning
 * today to the new pool's end and a handout covers a child for the whole of it.
 *
 * The new pool's OWN seats are the continuing students only. `membership.seats` records that count,
 * which can be ZERO — see `mintPoolInTransaction`.
 *
 * ONE TRANSACTION, on `dbPool`. The trigger's replay guard is "a Membership or a MembershipAddOn
 * exists for this invoice", so the pool and the chain have to land together or a retry after a
 * crash between them would report the invoice fulfilled with its empty seats missing. Same rule as
 * the two helpers this composes, for the same reason — see `client.ts`.
 *
 * Not idempotent: the caller's guard is what makes retries safe.
 */
export async function mintRenewalSeats(
  database: Database,
  { invoiceId, organizationId, seats, emptySeats, bridgePools, startDate, endDate, paidAt }: MintRenewalSeatsArgs,
): Promise<MintRenewalSeatsResult> {
  if (!Number.isInteger(seats) || seats < 1) {
    throw new Error(`Cannot mint ${seats} seats for invoice ${invoiceId}: seats must be a positive integer`)
  }

  if (!Number.isInteger(emptySeats) || emptySeats < 0 || emptySeats > seats) {
    throw new Error(`Cannot bridge ${emptySeats} of ${seats} seats for invoice ${invoiceId}`)
  }

  /**
   * A bridge pool that closed while the invoice sat unpaid has nothing left to ride: a segment on
   * it would end before it began. The checkout's gateway window is capped an hour before the live
   * pool's end precisely so this stays rare, and the days lost are bounded by that window.
   */
  const ridablePools = bridgePools.filter((pool) => pool.endDate > paidAt)

  // Nothing to ride today means the empty seats start with the new pool — the pre-bridge shape,
  // and identical in effect for a lapsed pool, whose new window opens at payment anyway.
  const bridgedSeats = ridablePools.length > 0 ? emptySeats : 0

  return database.transaction(async (tx) => {
    const pool = await mintPoolInTransaction(tx, {
      invoiceId,
      organizationId,
      seats: seats - bridgedSeats,
      startDate,
      endDate,
    })

    if (bridgedSeats === 0) {
      return { ...pool, addOns: [] }
    }

    const chain = await mintAddOnSeatsInTransaction(tx, {
      invoiceId,
      organizationId,
      seats: bridgedSeats,
      pools: [
        ...ridablePools,
        { id: pool.membership.id, startDate: pool.membership.startDate, endDate: pool.membership.endDate },
      ],
      paidAt,
      // Empty by definition. The invoice's assignment rows are the CONTINUING students, already
      // stamped onto the pool's own seats above.
      assignChildren: false,
    })

    return {
      membership: pool.membership,
      addOns: chain.addOns,
      seatsCreated: pool.seatsCreated + chain.seatsCreated,
      assignedChildIds: pool.assignedChildIds,
      skippedChildIds: pool.skippedChildIds,
      releasedTrialSeatIds: [...pool.releasedTrialSeatIds, ...chain.releasedTrialSeatIds],
    }
  })
}
